import { handleError } from 'globals/errors';
import { ChatSessionsApiResponse } from 'core_main/api/sessions/types';
import { fetchCurrentTrackingToken } from 'core_main/api/tracking';
import { Store } from 'core_main/types';
import { updateStore } from 'core_main/store';
import { HandoffConversationParameters, HandoffMessage, HandoffConversationCallbackResponse } from './types';

/**
 * Validation limits for handoff parameters
 */
const HANDOFF_LIMITS = {
  MIN_MESSAGES: 2,
  MAX_MESSAGES: 100,
  MAX_BODY_LENGTH: 10240,
} as const;

/**
 * Validates a single handoff message
 * @param message - Message to validate
 * @param index - Index of the message in the array (for error messages)
 * @returns Error object if validation fails, undefined if valid
 */
const validateHandoffMessage = (
  message: HandoffMessage,
  index: number,
): Error | undefined => {
  if (!message || typeof message !== 'object') {
    return new Error(`Message at index ${index} must be an object`);
  }

  const { date, direction, body } = message;

  // Validate date
  if (!date || typeof date !== 'string') {
    return new Error(`Message at index ${index}: 'date' must be a non-empty string`);
  }

  // Validate ISO 8601 date format
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) {
    return new Error(
      `Message at index ${index}: 'date' must be a valid ISO 8601 date string`,
    );
  }

  // Validate direction
  if (direction !== 'in' && direction !== 'out') {
    return new Error(
      `Message at index ${index}: 'direction' must be either 'in' or 'out'`,
    );
  }

  // Validate body
  if (typeof body !== 'string') {
    return new Error(`Message at index ${index}: 'body' must be a string`);
  }

  const trimmedBody = body.trim();
  if (trimmedBody === '') {
    return new Error(`Message at index ${index}: 'body' cannot be empty or whitespace only`);
  }

  if (trimmedBody.length > HANDOFF_LIMITS.MAX_BODY_LENGTH) {
    return new Error(
      `Message at index ${index}: 'body' must be ${HANDOFF_LIMITS.MAX_BODY_LENGTH} characters or less (found ${trimmedBody.length})`,
    );
  }

  return undefined;
};

/**
 * Validates handoff parameters and returns an error if validation fails
 * @param parameters - Handoff conversation parameters to validate
 * @returns Error object if validation fails, undefined if valid
 */
export const validateHandoffParameters = (
  parameters: HandoffConversationParameters,
): Error | undefined => {
  if (!parameters) {
    return new Error('Handoff parameters are required');
  }

  const { messages, from, active } = parameters;

  // Validate messages array
  if (!Array.isArray(messages)) {
    return new Error('Messages must be an array');
  }

  const messageCount = messages.length;

  // Validate message count
  if (messageCount < HANDOFF_LIMITS.MIN_MESSAGES) {
    return new Error(
      `At least ${HANDOFF_LIMITS.MIN_MESSAGES} messages are required (found ${messageCount})`,
    );
  }

  if (messageCount > HANDOFF_LIMITS.MAX_MESSAGES) {
    return new Error(
      `Maximum ${HANDOFF_LIMITS.MAX_MESSAGES} messages allowed (found ${messageCount})`,
    );
  }

  // Validate message directions (must have both inbound and outbound)
  const hasInbound = messages.some((msg) => msg.direction === 'in');
  const hasOutbound = messages.some((msg) => msg.direction === 'out');

  if (!hasInbound) {
    return new Error(
      'A handoff conversation must include at least one inbound message from the customer to establish context',
    );
  }

  if (!hasOutbound) {
    return new Error(
      'A handoff conversation must include at least one outbound message to show the conversation flow',
    );
  }

  // Validate 'from' parameter when outbound messages exist
  if (hasOutbound && (!from || typeof from !== 'string' || from.trim() === '')) {
    return new Error(
      "'from' parameter is required when outbound messages are present",
    );
  }

  // Validate active parameter
  if (typeof active !== 'boolean') {
    return new Error("'active' must be a boolean value");
  }

  // Validate each message
  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    const messageError = validateHandoffMessage(message, i);
    if (messageError) {
      return messageError;
    }
  }

  return undefined;
};

type UpdateStoreFunction = <M extends keyof Store>(property: M, data: Store[M]) => void;

interface ReIdentifyDependencies {
  fetchTrackingToken: () => Promise<{
    data?: {
      relationships?: {
        customer?: {
          data?: { id?: string };
        };
      };
    };
  }>;
  updateStore: UpdateStoreFunction;
  handleError: (error: Error, options: { message: string }) => void;
}

const defaultReIdentifyDeps: ReIdentifyDependencies = {
  fetchTrackingToken: fetchCurrentTrackingToken,
  updateStore: updateStore as UpdateStoreFunction,
  handleError,
};

/**
 * Re-identifies the customer after a successful handoff
 * 
 * This method refreshes the customer identity by fetching the current tracking token.
 * This ensures the customer identity is refreshed after handoff, which may update the
 * tracking token with a customer ID if one becomes available.
 * 
 * Matches iOS pattern: reIdentifyAfterHandoff() -> logIn(jwt: currentTrackingToken)
 * 
 * @param response - API response from handoff endpoint
 * @param deps - Optional dependencies object for dependency injection (defaults to real implementations)
 * @returns Promise that resolves with the same response (for chaining)
 */
export const reIdentifyAfterHandoff = (
  response: ChatSessionsApiResponse,
  deps: Partial<ReIdentifyDependencies> = {},
): Promise<ChatSessionsApiResponse> => {
  const {
    fetchTrackingToken = defaultReIdentifyDeps.fetchTrackingToken,
    updateStore: updateStoreFn = defaultReIdentifyDeps.updateStore,
    handleError: handleErrorFn = defaultReIdentifyDeps.handleError,
  } = deps;

  return fetchTrackingToken()
    .then((trackingTokenResponse) => {
      // Update store with latest customer identity
      const customerId = trackingTokenResponse?.data?.relationships?.customer?.data?.id;
      if (customerId) {
        updateStoreFn('customerId', customerId);
      }
      // Return original handoff response for chaining
      return response;
    })
    .catch((error) => {
      // Log error but don't fail handoff - identity refresh is best effort
      handleErrorFn(error, {
        message: 'Failed to refresh customer identity after handoff (continuing):',
      });
      // Return original response anyway - handoff succeeded
      return response;
    });
};

/**
 * Creates a callback response from API response
 * Matches the pattern used by createConversationCallbackResponse
 * @param response - API response from handoff endpoint (standard chat session response)
 * @returns Callback response object
 */
export const createHandoffCallbackResponse = (
  response: ChatSessionsApiResponse,
): HandoffConversationCallbackResponse => {
  const { id: conversationId } = response.data;
  const { createdAt, lockedAt, isInAssistantMode } = response.data.attributes;

  return {
    conversationId,
    createdAt,
    ended: !!lockedAt,
    isInAssistantMode,
    session: response.data,
  };
};
